import { I18n } from '@calendly/i18n';
import { CloseCircleIcon } from '@calendly/ui/components/icons';
import { BodyText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import { colorTextDestructive } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { isValueEmpty } from './common';
const gridContainerIsPhoneCss = "g7qcj60";
const gridContainerIsOneColumnCss = "g182tg3i";
const gridContainerIsSmallPhoneCss = "g189xta9";
const gridContainerCss = "g1jmt2si";
const GridContainer = forwardRef((props, ref) => {
  const {
    isOneColumn,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isSmallPhone = useDeviceType('isSmallPhone');
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(gridContainerCss, {
    [gridContainerIsPhoneCss]: isPhone,
    [gridContainerIsOneColumnCss]: isOneColumn,
    [gridContainerIsSmallPhoneCss]: isSmallPhone
  }, props.className)} />;
});
const gridItemCss = "g1r1ztcz";
const GridItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(gridItemCss, props.className)} />;
});
const iconContainerCss = "ieq7gdz";
const IconContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(iconContainerCss, props.className)} />;
});
const textContainerCss = "t11lojt4";
const TextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, props.className)} />;
});

// paypal_ppcp is PayPal's newer "Partner Commerce Platform" integration. It should be
// treated identically to 'paypal' for display purposes.
const PAYMENT_PROVIDER_ALIASES = {
  paypal_ppcp: 'paypal'
};
const normalizePaymentProviders = value => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(provider => PAYMENT_PROVIDER_ALIASES[provider] || provider);
};

// Using variables to dynamically fill in the i18n strings doesn't seem to work. Might
// be because they don't always all render, but we'll address it once this is no longer
// an experiment.
// i18n-tasks-use t('publisher.trial_loss.features.multiple_event_types.description')
// i18n-tasks-use t('publisher.trial_loss.features.group_event_types.description')
// i18n-tasks-use t('publisher.trial_loss.features.multiple_calendars.description')
// i18n-tasks-use t('publisher.trial_loss.features.email_reminders.description')
// i18n-tasks-use t('publisher.trial_loss.features.workflows.description')
// i18n-tasks-use t('publisher.trial_loss.features.text_reminders.description')
// i18n-tasks-use t('publisher.trial_loss.features.email_follow_ups.description')
// i18n-tasks-use t('publisher.trial_loss.features.payments.description')
// i18n-tasks-use t('publisher.trial_loss.features.meeting_recaps.description')
const getTranslation = (feature, value) => {
  if (feature === 'payments') {
    const providers = normalizePaymentProviders(value);
    const hasStripe = providers.includes('stripe');
    const hasPaypal = providers.includes('paypal');
    if (hasStripe && hasPaypal) {
      return I18n.t('publisher.trial_loss.features.payments.description.both');
    }
    if (hasStripe) {
      return I18n.t('publisher.trial_loss.features.payments.description.stripe');
    }
    if (hasPaypal) {
      return I18n.t('publisher.trial_loss.features.payments.description.paypal');
    }
    return null;
  }
  return I18n.t(`publisher.trial_loss.features.${feature}.description`, {
    count: value
  });
};

// i18n-tasks-use t('publisher.trial_loss.features.multiple_event_types.name')
// i18n-tasks-use t('publisher.trial_loss.features.group_event_types.name')
// i18n-tasks-use t('publisher.trial_loss.features.multiple_calendars.name')
// i18n-tasks-use t('publisher.trial_loss.features.email_reminders.name')
// i18n-tasks-use t('publisher.trial_loss.features.workflows.name')
// i18n-tasks-use t('publisher.trial_loss.features.text_reminders.name')
// i18n-tasks-use t('publisher.trial_loss.features.email_follow_ups.name')
// i18n-tasks-use t('publisher.trial_loss.features.payments.name')
// i18n-tasks-use t('publisher.trial_loss.features.meeting_recaps.name')
const FeatureItem = ({
  feature,
  value
}) => {
  if (isValueEmpty(value)) {
    return null;
  }
  const translation = getTranslation(feature, value);
  if (!translation) {
    return null;
  }
  return <GridItem>
      <IconContainer>
        <CloseCircleIcon color={colorTextDestructive} />
      </IconContainer>
      <TextContainer>
        <BodyText weight="bold">
          {I18n.t(`publisher.trial_loss.features.${feature}.name`)}
        </BodyText>
        <BodyText>
          {translation}
        </BodyText>
      </TextContainer>
    </GridItem>;
};
export const FeatureGrid = ({
  featureData,
  userEnabledFeatures
}) => {
  if (!featureData || !userEnabledFeatures) return null;
  const isOneColumn = userEnabledFeatures.length < 4;
  return <GridContainer isOneColumn={isOneColumn}>
      {userEnabledFeatures.map(feature => <FeatureItem key={feature} feature={feature} value={featureData[feature]} />)}
    </GridContainer>;
};

require("./feature_grid.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./feature_grid.js");